(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Lightbox = global.NewHomeSource.Lightbox || {};

  class PillHandler {
    /**
     * Initializes a new instance of the PillHandler.
     */
    constructor() {
      this.state = null;
      this.carousel = null;
      this.rootNode = null;
      this.pillClickHandler = null;
      this.pillsContainer = null;
      this.pillList = null;
      this.arrowLeft = null;
      this.arrowRight = null;
      this.arrowHandler = null;
      this.pillResizeObserver = null;
    }

    /**
     * Initializes the pill handler with state and carousel references.
     *
     * @param {Object} state - The lightbox state object.
     * @param {Object} carousel - The carousel instance.
     */
    initialize(state, carousel) {
      this.state = state;
      this.carousel = carousel;
      this.rootNode = state.get('rootNode');
      this.attachPillClickHandlers();
      this.initPillScrollArrows();
    }

    /**
     * Attaches click handlers to type pills so they navigate to the first slide of that type.
     * Pill elements are queried within the lightbox root to ensure scoping to the current instance.
     */
    attachPillClickHandlers() {
      if (!this.rootNode) {
        return;
      }

      this.pillClickHandler = this.handlePillClick.bind(this);

      const pills = this.rootNode.querySelectorAll('[data-pill]');
      pills.forEach((pill) => {
        pill.addEventListener('click', this.pillClickHandler);
      });
    }

    /**
     * Removes all click handlers from type pills.
     * Cleans up listeners attached during attachPillClickHandlers.
     */
    removePillClickHandlers() {
      if (!this.rootNode) {
        return;
      }

      const pills = this.rootNode.querySelectorAll('[data-pill]');
      pills.forEach((pill) => {
        if (this.pillClickHandler) {
          pill.removeEventListener('click', this.pillClickHandler);
        }
      });

      this.rootNode = null;
      this.pillClickHandler = null;
    }

    /**
     * Handles a pill click event by navigating the carousel to the first slide matching the pill's target type.
     *
     * @param {Event} event - The click event from a pill element.
     */
    handlePillClick(event) {
      const pill = event.currentTarget;
      const targetType = pill.dataset.targetType;

      if (!targetType) {
        return;
      }

      // Normalize 3D tour mapping: pill target '3dTour' should match carousel type 'ExternalMedium'
      const normalizedType = targetType === '3dTour' ? 'ExternalMedium' : targetType;

      const targetIndex = this.findFirstIndexByType(normalizedType);

      // Only navigate if a matching slide exists; otherwise, silently do nothing
      if (targetIndex >= 0) {
        this.carousel.scrollByIndex(targetIndex);
      }
    }

    /**
     * Finds the index of the first carousel slide matching a given type.
     *
     * @param {string} type - The data-type value to match (e.g., 'Elevation', 'Interior', 'Video', 'ExternalMedium').
     * @returns {number} The index of the first matching slide, or -1 if not found.
     */
    findFirstIndexByType(type) {
      const carousel = this.rootNode.querySelector('[data-component="lightbox-container-carousel"]');
      if (!carousel) {
        return -1;
      }

      const slides = carousel.querySelectorAll('[data-column]');
      const match = Array.from(slides).find((slide) => slide.dataset.type === type);
      return match ? Number.parseInt(match.dataset.column, 10) : -1;
    }

    /**
     * Cleans up the pill handler by removing event listeners and clearing references.
     */
    destroy() {
      this.removePillClickHandlers();
      this.removePillScrollArrows();
      this.state = null;
      this.carousel = null;
    }

    /**
     * Sets up the overflow scroll arrows for the pill list.
     * Binds click listeners to the left/right arrow buttons and observes the list for
     * size changes so arrow visibility stays current.
     */
    initPillScrollArrows() {
      this.pillsContainer = this.rootNode.querySelector('[data-popular-pills-container]');
      this.pillList = this.rootNode.querySelector('[data-popular-pills-list]');
      this.arrowLeft = this.rootNode.querySelector('[data-popular-searches-pills-button-left]');
      this.arrowRight = this.rootNode.querySelector('[data-popular-searches-pills-button-right]');

      if (!this.pillsContainer || !this.pillList || !this.arrowLeft || !this.arrowRight) {
        return;
      }

      this.arrowLeft.dataset.arrowLeft = '';
      this.arrowRight.dataset.arrowRight = '';
      this.pillList.dataset.scroll = '';
      this.pillList.querySelectorAll('[data-pill]').forEach((pillElement) => {
        const currentPill = pillElement;
        currentPill.dataset.elementScrolled = '';
      });

      this.updateArrowMoveValues();

      if (global.NewHomeSource.Modules && global.NewHomeSource.Modules.ArrowHandler) {
        this.arrowHandler = new global.NewHomeSource.Modules.ArrowHandler({
          container: this.pillsContainer
        });
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.pillResizeObserver = new ResizeObserver(() => {
          this.updateArrowMoveValues();
          this.pillsContainer.dispatchEvent(new Event('recalculateLimits'));
        });
        this.pillResizeObserver.observe(this.pillList);
      }
    }

    /**
     * Returns the horizontal scroll increment used by pill arrows.
     * Matches Results page behavior by advancing approximately one pill at a time.
     *
     * @returns {number} Scroll amount in pixels.
     */
    getPillScrollAmount() {
      const firstPill = this.pillList.querySelector('[data-pill]');
      if (!firstPill) {
        return this.pillList.offsetWidth;
      }

      const listStyle = global.getComputedStyle(this.pillList);
      const gap = Number.parseInt(listStyle.columnGap || listStyle.gap || 0, 10) || 0;
      return firstPill.offsetWidth + gap;
    }

    /**
     * Shows or hides the left/right scroll arrows based on the current scroll position
     * and whether the pill list overflows its container.
     */
    updateArrowMoveValues() {
      const moveAmount = this.getPillScrollAmount();
      this.arrowLeft.dataset.moveArrow = `${-moveAmount}`;
      this.arrowRight.dataset.moveArrow = `${moveAmount}`;
    }

    /**
     * Removes scroll arrow event listeners and disconnects the resize observer.
     */
    removePillScrollArrows() {
      if (this.arrowHandler && this.arrowHandler.dispose) {
        this.arrowHandler.dispose();
      }

      if (this.pillResizeObserver) {
        this.pillResizeObserver.disconnect();
        this.pillResizeObserver = null;
      }

      if (this.arrowLeft) {
        delete this.arrowLeft.dataset.arrowLeft;
        delete this.arrowLeft.dataset.moveArrow;
      }
      if (this.arrowRight) {
        delete this.arrowRight.dataset.arrowRight;
        delete this.arrowRight.dataset.moveArrow;
      }
      if (this.pillList) {
        delete this.pillList.dataset.scroll;
        this.pillList.querySelectorAll('[data-pill]').forEach((pillElement) => {
          const currentPill = pillElement;
          delete currentPill.dataset.elementScrolled;
        });
      }

      this.pillsContainer = null;
      this.pillList = null;
      this.arrowLeft = null;
      this.arrowRight = null;
      this.arrowHandler = null;
    }
  }

  global.NewHomeSource.Lightbox.PillHandler = PillHandler;
}(window));
